// NOTE: This file is stable and usually should not be modified.
// It is important that all functionality in this file is preserved, and should only be modified if explicitly requested.

import { LogOut, UserPlus, Library, Settings, Swords, User, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx';
import { useLoggedInAccounts, type Account } from '@/hooks/useLoggedInAccounts';
import { genUserName } from '@/lib/genUserName';


import { RelaySelector } from '../RelaySelector';
import { ThemeSwitcher } from '../ThemeSwitcher';

interface AccountSwitcherProps {
  onAddAccountClick: () => void;
}

export function AccountSwitcher({ onAddAccountClick }: AccountSwitcherProps) {
  const { currentUser, otherUsers, setLogin, removeLogin } = useLoggedInAccounts();

  if (!currentUser) return null;

  const getDisplayName = (account: Account): string => {
    return account.metadata.name ?? genUserName(account.pubkey);
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button className='rounded-full hover:bg-accent transition-all'>
          <Avatar className='w-10 h-10'>
            <AvatarImage src={currentUser.metadata.picture} alt={getDisplayName(currentUser)} />
            <AvatarFallback>
              <User className="h-5 w-5" />
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-56 p-2 animate-scale-in'>
        <DropdownMenuItem
          asChild
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
        >
          <Link to={`/profile/${nip19.npubEncode(currentUser.pubkey)}`}>
            <Library className='w-4 h-4' />
            <span>My decks</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          asChild
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
        >
          <Link to="/browse">
            <Layers className='w-4 h-4' />
            <span>Browse Decks</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          asChild
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
        >
          <Link to="/build">
            <Swords className="h-4 w-4" />
            <span>Build</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          asChild
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
        >
          <Link to="/settings">
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <ThemeSwitcher />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className='px-2 py-1.5 space-y-2'>
          <Label htmlFor="relay-selector" className="text-xs text-muted-foreground">Relay Selector</Label>
          <RelaySelector id="relay-selector" className="w-full" />
        </div>
        <DropdownMenuSeparator />
        {otherUsers.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className='font-medium text-sm px-2 py-1.5'>Switch Account</div>
            {otherUsers.map((user) => (
              <DropdownMenuItem
                key={user.id}
                onClick={() => setLogin(user.id)}
                className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
              >
                <Avatar className='w-8 h-8'>
                  <AvatarImage src={user.metadata.picture} alt={getDisplayName(user)} />
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <div className='flex-1 truncate'>
                  <p className='text-sm font-medium'>{getDisplayName(user)}</p>
                </div>
                {user.id === currentUser.id && <div className='w-2 h-2 rounded-full bg-primary'></div>}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          onClick={onAddAccountClick}
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
        >
          <UserPlus className='w-4 h-4' />
          <span>Add another account</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => removeLogin(currentUser.id)}
          className='flex items-center gap-2 cursor-pointer p-2 rounded-md text-red-500'
        >

          <LogOut className='w-4 h-4' />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
